import type { WorkOrderEntry } from './workOrder'
import { workOrderAdditionalFields } from './workOrderFields'
import { workOrderPlainText } from './workOrderText'

export interface WorkOrderEvidenceField {
  id: string
  text: string
}

// These strings match cmms-connectors/indexer/render.py's indexed evidence,
// independently of the display locale and the PDF's layout/order.
export function workOrderEvidence(
  entry: WorkOrderEntry
): WorkOrderEvidenceField[] {
  const wo = entry.data.record
  const fields: WorkOrderEvidenceField[] = []
  const add = (id: string, value: string, text = value) => {
    if (value) fields.push({ id, text })
  }
  add(
    'title',
    wo.wonum,
    `Work order ${wo.wonum}: ${workOrderPlainText(wo.description)}`
  )
  add('description', workOrderPlainText(wo.description_longdescription))
  add(
    'status',
    wo.status,
    `Status: ${wo.status_description || wo.status} (${wo.status})`
  )
  if (typeof wo.istask === 'boolean')
    add('task', String(wo.istask), `Task record (istask): ${wo.istask}`)
  add(
    wo.wogroup === wo.wonum ? 'title' : 'group',
    wo.wogroup,
    `WO group (wogroup): ${wo.wogroup}`
  )
  add('jobPlan', wo.jpnum, `Job plan (jpnum): ${wo.jpnum}`)
  add(
    'statusDate',
    wo.statusdate,
    `Status updated (statusdate): ${wo.statusdate}`
  )
  const type = [wo.worktype, wo.woclass_description || wo.woclass]
    .filter(Boolean)
    .join(', ')
  add('type', type, `Type: ${type}`)
  add('location', wo.location, `Location: ${wo.location}`)
  add('asset', wo.assetnum, `Asset: ${wo.assetnum}`)
  add('parent', wo.parent, `Parent: ${wo.parent}`)
  add('pm', wo.pmnum, `PM: ${wo.pmnum}`)
  add(
    'reported',
    wo.reportdate,
    `Reported ${wo.reportdate.slice(0, 10)}${wo.reportedby ? ` by ${wo.reportedby}` : ''}`
  )
  add(
    'targetStart',
    wo.targstartdate,
    `Target start: ${wo.targstartdate.slice(0, 10)}`
  )
  add(
    'targetFinish',
    wo.targcompdate,
    `Target finish: ${wo.targcompdate.slice(0, 10)}`
  )
  add('actualStart', wo.actstart, `Actual start: ${wo.actstart.slice(0, 10)}`)
  add(
    'actualFinish',
    wo.actfinish,
    `Actual finish: ${wo.actfinish.slice(0, 10)}`
  )
  const logs = wo.worklog
    .map((log, index) => {
      const body = [log.description, log.description_longdescription]
        .map(workOrderPlainText)
        .filter(Boolean)
        .join(' ')
      return {
        id: `worklog.${index}`,
        date: log.createdate,
        body,
        text: `- ${log.createdate.slice(0, 10)}${log.createby ? ` ${log.createby}` : ''}${log.logtype ? ` (${log.logtype})` : ''}: ${body}`,
      }
    })
    .filter((log) => log.body)
    .sort((a, b) => a.date.localeCompare(b.date))
  if (logs.length) add('worklogsLabel', 'Worklogs:')
  for (const log of logs) add(log.id, log.text)
  for (const field of workOrderAdditionalFields(entry))
    add(field.id, field.evidence)
  return fields
}
